/*!
 * StormMode - a one-flip closure / delay / notice banner.
 * Vendly LLC. Rentable micro-app. Dependency-free, no build step.
 *
 * ---------------------------------------------------------------------------
 * IDEA
 *   A slim banner that appears ONLY when a business has something to announce -
 *   a weather closure, a delayed opening, or a quick notice. It reads a tiny
 *   status file (status.json). Flip "active" to true and set the message, and
 *   the banner shows on the site within a minute. Flip it to false and it
 *   disappears. No code changes to turn it on or off.
 *
 * HOW IT WORKS
 *   1. Host a status file somewhere Vendly (or the owner) can edit, e.g.
 *        https://YOUR-HOST/palmetto-status.json
 *      with contents:
 *        { "active": false, "level": "closed", "message": "", "link": "" }
 *   2. On the site, add the container + config + script:
 *        <div id="stormmode"></div>
 *        <script>
 *          window.StormModeConfig = {
 *            target: "#stormmode",
 *            statusUrl: "https://YOUR-HOST/palmetto-status.json",
 *            pollSeconds: 120,        // re-check every 2 minutes
 *            dismissible: true
 *          };
 *        </script>
 *        <script src="stormmode.js" defer></script>
 *
 *   To turn it on: edit status.json ->
 *        { "active": true, "level": "closed",
 *          "message": "Closed today due to snow. Back tomorrow at 9am.", "link": "" }
 *
 *   levels: "closed" (red), "delay" (amber), "info" (blue).
 *   Optional "link" adds a "Details" link on the banner.
 *
 * You can also skip the file and pass an inline status for testing:
 *   window.StormModeConfig = { target:"#stormmode",
 *     status: { active:true, level:"delay", message:"Opening at noon today." } };
 * ---------------------------------------------------------------------------
 */
(function () {
  "use strict";

  var DEFAULTS = {
    target: "#stormmode",
    statusUrl: null,      // URL to a JSON status file
    status: null,         // OR an inline status object (skips fetch)
    pollSeconds: 120,     // how often to re-check the file
    dismissible: true,
    position: "top"       // "top" = fixed full-width bar; "inline" = in the container flow
  };

  var LEVELS = {
    closed: { bg: "#b3261e", fg: "#ffffff" },
    delay:  { bg: "#b26a00", fg: "#ffffff" },
    info:   { bg: "#1a4fb4", fg: "#ffffff" }
  };

  function merge(user) {
    var c = {};
    for (var k in DEFAULTS) c[k] = DEFAULTS[k];
    for (var j in user) if (user.hasOwnProperty(j)) c[j] = user[j];
    return c;
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (ch) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch];
    });
  }

  // Hardening (2026-07-11): the status file is edited/hosted separately, so treat
  // its "link" as untrusted. Only allow safe schemes so a "javascript:" or
  // "data:text/html" link can never run script when a visitor clicks "Details".
  // Returns "" (no link rendered) for anything not on the allowlist.
  function safeUrl(raw) {
    if (typeof raw !== "string") return "";
    var v = raw.trim();
    if (!v) return "";
    // Relative links are fine (start with / . # ? or a bare path segment).
    if (/^[\/.#?]/.test(v)) return v;
    // Absolute links must use an allowed scheme.
    if (/^(https?|mailto|tel|sms):/i.test(v)) return v;
    // Anything with a scheme we do not allow (javascript:, data:, vbscript:, ...) is dropped.
    if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return "";
    // No scheme and not clearly relative: allow as a relative path.
    return v;
  }

  function injectCss() {
    if (document.getElementById("stormmode-css")) return;
    var css =
      ".sm-bar{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;" +
      "display:flex;align-items:center;justify-content:center;gap:12px;padding:11px 16px;font-size:15px;" +
      "font-weight:600;line-height:1.35;text-align:center;box-sizing:border-box;width:100%}" +
      ".sm-fixed{position:fixed;top:0;left:0;right:0;z-index:99999}" +
      ".sm-bar a{color:inherit;text-decoration:underline;font-weight:700}" +
      ".sm-close{background:none;border:none;color:inherit;font-size:20px;line-height:1;cursor:pointer;" +
      "opacity:.85;padding:0 2px;flex:0 0 auto}" +
      ".sm-close:hover{opacity:1}" +
      ".sm-msg{flex:1 1 auto}";
    var el = document.createElement("style");
    el.id = "stormmode-css";
    el.textContent = css;
    document.head.appendChild(el);
  }

  function dismissedKey(cfg, status) {
    // Key on the message so a NEW notice reappears even if a prior one was dismissed.
    return "stormmode-dismissed:" + (cfg.statusUrl || "inline") + ":" + (status.message || "");
  }

  function render(container, cfg, status) {
    container.innerHTML = "";
    if (!status || !status.active || !status.message) return;

    if (cfg.dismissible) {
      try {
        if (sessionStorage.getItem(dismissedKey(cfg, status)) === "1") return;
      } catch (e) { /* sessionStorage may be blocked; just show the banner */ }
    }

    var level = LEVELS[status.level] || LEVELS.info;
    var bar = document.createElement("div");
    bar.className = "sm-bar" + (cfg.position === "top" ? " sm-fixed" : "");
    bar.style.background = level.bg;
    bar.style.color = level.fg;
    bar.setAttribute("role", "status");

    var html = '<span class="sm-msg">' + escapeHtml(status.message);
    // Hardening (2026-07-11): scheme-allowlist the link before rendering it.
    var link = safeUrl(status.link);
    if (link) html += ' <a href="' + escapeHtml(link) + '" rel="noopener noreferrer">Details</a>';
    html += "</span>";
    if (cfg.dismissible) html += '<button class="sm-close" type="button" aria-label="Dismiss">&times;</button>';
    bar.innerHTML = html;

    if (cfg.dismissible) {
      bar.querySelector(".sm-close").addEventListener("click", function () {
        try { sessionStorage.setItem(dismissedKey(cfg, status), "1"); } catch (e) {}
        container.innerHTML = "";
      });
    }
    container.appendChild(bar);
  }

  function fetchStatus(url) {
    // Cache-bust so an edit to the file shows up quickly.
    var bust = url + (url.indexOf("?") === -1 ? "?" : "&") + "_=" + Date.now();
    return fetch(bust, { cache: "no-store" }).then(function (r) {
      if (!r.ok) throw new Error("status " + r.status);
      return r.json();
    });
  }

  function init(userCfg) {
    var cfg = merge(userCfg || {});
    injectCss();
    var container = document.querySelector(cfg.target);
    if (!container) {
      if (window.console) console.warn("[StormMode] target not found:", cfg.target);
      return;
    }

    function cycle() {
      if (cfg.status) { render(container, cfg, cfg.status); return; }
      if (!cfg.statusUrl) return;
      fetchStatus(cfg.statusUrl)
        .then(function (status) { render(container, cfg, status); })
        .catch(function () { /* fail safe: on any error, show nothing, never break the site */ });
    }

    cycle();
    if (!cfg.status && cfg.statusUrl) {
      setInterval(cycle, Math.max(30, cfg.pollSeconds) * 1000);
    }
  }

  window.StormMode = { init: init };
  function autostart() { if (window.StormModeConfig) init(window.StormModeConfig); }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autostart);
  } else {
    autostart();
  }
})();
